// Sleep timer state machine (player plan §3). Pure and DOM-free — the
// Alpine component owns the timers/intervals and the audio element;
// this module owns the STATE rules. Per-session only: never persisted
// (the Android counterpart's persisted sleep setting is exactly the
// mistake plan §7 removes).

type SleepMode = "off" | "minutes" | "end-of-chapter" | "fading";

export interface SleepState {
  mode: SleepMode;
  // Epoch ms deadline for "minutes" mode; null otherwise.
  endsAtMs: number | null;
  // Chapter index the end-of-chapter timer was armed at; null otherwise.
  chapterIndex: number | null;
  // §17 chain: when set on an end-of-chapter arm, leaving the chapter
  // does NOT fade — it starts a minutes countdown of this length
  // ("end of chapter, then 15 min").
  followUpMinutes: number | null;
}

export const SLEEP_OFF: SleepState = {
  mode: "off",
  endsAtMs: null,
  chapterIndex: null,
  followUpMinutes: null,
};

export function armMinutes(nowMs: number, minutes: number): SleepState {
  return {
    mode: "minutes",
    endsAtMs: nowMs + minutes * 60_000,
    chapterIndex: null,
    followUpMinutes: null,
  };
}

export function armEndOfChapter(
  chapterIndex: number,
  followUpMinutes: number | null = null,
): SleepState {
  return { mode: "end-of-chapter", endsAtMs: null, chapterIndex, followUpMinutes };
}

// armClockTime (§17): "stop at 23:00" — resolves HH:MM to its NEXT
// occurrence (today if still ahead, otherwise tomorrow) and arms the
// ordinary minutes machinery against that epoch deadline. Returns null
// for unparseable strings; the caller keeps the old state then.
export function armClockTime(nowMs: number, hhmm: string): SleepState | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  const target = new Date(nowMs);
  target.setHours(h, min, 0, 0);
  if (target.getTime() <= nowMs) target.setDate(target.getDate() + 1);
  return { mode: "minutes", endsAtMs: target.getTime(), chapterIndex: null, followUpMinutes: null };
}

// sleepDue is true when a minutes-mode deadline has passed.
export function sleepDue(state: SleepState, nowMs: number): boolean {
  return state.mode === "minutes" && state.endsAtMs !== null && nowMs >= state.endsAtMs;
}

// sleepChapterFired is true when an end-of-chapter timer's chapter has
// been left — ANY change from the armed index fires it (forward into
// the next chapter naturally, or a manual jump away from it).
export function sleepChapterFired(state: SleepState, currentIndex: number): boolean {
  return (
    state.mode === "end-of-chapter" &&
    state.chapterIndex !== null &&
    currentIndex !== state.chapterIndex
  );
}

export function sleepRemainingMs(state: SleepState, nowMs: number): number | null {
  if (state.mode !== "minutes" || state.endsAtMs === null) return null;
  return Math.max(0, state.endsAtMs - nowMs);
}

// fadeVolume ramps linearly base → 0 across fadeMs and clamps beyond
// both ends.
export function fadeVolume(baseVolume: number, elapsedMs: number, fadeMs = 5000): number {
  const base = Math.min(1, Math.max(0, baseVolume));
  if (elapsedMs <= 0) return base;
  if (elapsedMs >= fadeMs) return 0;
  return Math.max(0, base * (1 - elapsedMs / fadeMs));
}

export function formatRemaining(ms: number): string {
  const totalSeconds = Math.ceil(Math.max(0, ms) / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function sleepLabel(state: SleepState, nowMs: number): string {
  switch (state.mode) {
    case "off":
      return "Off";
    case "end-of-chapter":
      return state.followUpMinutes !== null ? `Ch + ${state.followUpMinutes}m` : "End of chapter";
    case "fading":
      return "Fading";
    case "minutes": {
      const remaining = sleepRemainingMs(state, nowMs);
      return remaining === null ? "Off" : formatRemaining(remaining);
    }
  }
}

// Dropdown vocabulary — the templ sleep dropdown iterates this (the
// component exposes it; templ cannot import TS) and chooseSleep()
// consumes it. One source for both the rows and their behavior.
export interface SleepChoice {
  key: string;
  label: string;
  mode: "off" | "minutes" | "end-of-chapter";
  minutes?: number;
  // chain: this end-of-chapter row arms a follow-up minutes countdown
  // ("end of chapter, then N min") instead of fading straight away.
  chain?: boolean;
}

export const SLEEP_CHOICES: readonly SleepChoice[] = [
  { key: "off", label: "Off", mode: "off" },
  { key: "5", label: "5 minutes", mode: "minutes", minutes: 5 },
  { key: "10", label: "10 minutes", mode: "minutes", minutes: 10 },
  { key: "15", label: "15 minutes", mode: "minutes", minutes: 15 },
  { key: "30", label: "30 minutes", mode: "minutes", minutes: 30 },
  { key: "45", label: "45 minutes", mode: "minutes", minutes: 45 },
  { key: "60", label: "60 minutes", mode: "minutes", minutes: 60 },
  { key: "eoch", label: "End of chapter", mode: "end-of-chapter" },
  {
    key: "eoch15",
    label: "End of chapter, then 15 min",
    mode: "end-of-chapter",
    minutes: 15,
    chain: true,
  },
];
